/**
 * Pure stat-tile value formatting + compare-delta computation (S4). No React,
 * no I/O — every branch is unit-testable in isolation. A missing or
 * non-numeric column NEVER falls through to `NaN` in the rendered text: it
 * comes back as an explicit `StatFormatError` so `StatTileView` can render
 * "why", matching the UI Quality Contract's "no blank/garbled surface" rule.
 */
import type { ColumnTypeInfo } from '../../charts/types/chartTypes';
import { getYAxisCandidates } from '../../charts/utils/columnTypeDetection';
import { resolveChartColumnRef } from '../../charts/utils/chartColumnRefs';
import type { StatCompare, StatFormat } from '../types/dashboardTypes';

export type StatFormatError =
  | { kind: 'missing-column'; column: string }
  | { kind: 'not-numeric'; column: string };

export type StatValueOutcome =
  | { ok: true; raw: number; text: string }
  | { ok: false; error: StatFormatError };

export type StatCompareOutcome =
  | { kind: 'none' }
  | { kind: 'delta'; deltaPercent: number; direction: 'up' | 'down' | 'flat'; text: string }
  | { kind: 'error'; error: StatFormatError };

const PLAIN_NUMBER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
// Same 1-decimal rounding as `trimDecimal` but WITH thousands grouping — a stat
// tile's percent format must group large values the same way the plain-number
// format does (12,345,678.9%, not 12345678.9%).
const PERCENT_NUMBER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

/** Rounds to `digits` decimals and drops a trailing `.0` (42 not 42.0). */
function trimDecimal(value: number, digits = 1): string {
  return String(Number(value.toFixed(digits)));
}

function formatPlainNumber(raw: number): string {
  return PLAIN_NUMBER.format(raw);
}

function formatPercent(raw: number, scale: 'fraction' | 'percent'): string {
  const pct = scale === 'fraction' ? raw * 100 : raw;
  return `${PERCENT_NUMBER.format(pct)}%`;
}

function formatCurrency(raw: number, code: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(raw);
  } catch {
    // Unrecognized/invalid ISO code — degrade gracefully instead of throwing.
    return `${code} ${formatPlainNumber(raw)}`;
  }
}

function formatDuration(raw: number, unit: 'ms' | 's' | 'min'): string {
  const totalMs = unit === 'ms' ? raw : unit === 's' ? raw * 1000 : raw * 60_000;
  const sign = totalMs < 0 ? '-' : '';
  const abs = Math.abs(totalMs);
  if (abs < 1000) return `${sign}${Math.round(abs)}ms`;
  if (abs < 60_000) return `${sign}${trimDecimal(abs / 1000)}s`;
  if (abs < 3_600_000) {
    const m = Math.floor(abs / 60_000);
    const s = Math.round((abs % 60_000) / 1000);
    return `${sign}${m}m${s ? ` ${s}s` : ''}`;
  }
  const h = Math.floor(abs / 3_600_000);
  const m = Math.round((abs % 3_600_000) / 60_000);
  return `${sign}${h}h${m ? ` ${m}m` : ''}`;
}

/** Formats an already-resolved numeric raw value per the tile's `StatFormat`. */
export function formatStatNumber(raw: number, format: StatFormat): string {
  switch (format.kind) {
    case 'number':
      return formatPlainNumber(raw);
    case 'percent':
      return formatPercent(raw, format.scale);
    case 'currency':
      return formatCurrency(raw, format.code);
    case 'duration':
      return formatDuration(raw, format.unit);
  }
}

/** Reads + validates a numeric cell; never returns NaN — an explicit error instead. */
function readNumericColumn(
  row: Record<string, unknown> | undefined,
  column: string,
): { ok: true; value: number } | { ok: false; error: StatFormatError } {
  if (!row || !(column in row)) return { ok: false, error: { kind: 'missing-column', column } };
  const cell = row[column];
  if (cell === null || cell === undefined) return { ok: false, error: { kind: 'not-numeric', column } };
  const num = typeof cell === 'number' ? cell : Number(cell);
  if (!Number.isFinite(num)) return { ok: false, error: { kind: 'not-numeric', column } };
  return { ok: true, value: num };
}

/** A stat tile's column-reference fields, as consumed by `resolveStatTileRefs`. */
export type StatTileColumnRefsInput = {
  valueColumn?: string;
  compare?: StatCompare;
  sparkline?: { timeColumn: string; valueColumn: string };
};

/** Same shape, every reference resolved to a row key. */
export type ResolvedStatTileRefs = {
  valueColumn: string | undefined;
  compare: StatCompare;
  sparkline: { timeColumn: string; valueColumn: string } | undefined;
};

/**
 * Resolves a stat tile's persisted column references (`valueColumn`, the
 * previous-period `compare.column`, `sparkline.timeColumn`/`valueColumn`)
 * against the CURRENT result set's columns — the same rule chart configs use
 * (exact row KEY wins, else a NAME that matches exactly one column; a name
 * shared by more than one, or not found at all, is left as-is so the
 * existing `missing-column`/`not-numeric` error path still reports it).
 * `valueColumn` falls back to the first numeric candidate when the tile
 * doesn't declare one — the same default `StatTileView`/`alertValuePreview`/
 * the HTML snapshot already applied inline. This is the ONE seam all three
 * call so they can never disagree about which column a stat tile reads.
 */
export function resolveStatTileRefs(
  tile: StatTileColumnRefsInput,
  columns: ColumnTypeInfo[],
): ResolvedStatTileRefs {
  const refColumns = columns.map((c) => ({ key: c.key, name: c.name }));

  const valueColumn = tile.valueColumn
    ? resolveChartColumnRef(tile.valueColumn, refColumns)
    : getYAxisCandidates(columns)[0]?.key;

  const compareIn = tile.compare ?? { kind: 'none' as const };
  const compare: StatCompare =
    compareIn.kind === 'previous-period'
      ? { ...compareIn, column: resolveChartColumnRef(compareIn.column, refColumns) }
      : compareIn;

  const sparkline = tile.sparkline
    ? {
        timeColumn: resolveChartColumnRef(tile.sparkline.timeColumn, refColumns),
        valueColumn: resolveChartColumnRef(tile.sparkline.valueColumn, refColumns),
      }
    : undefined;

  return { valueColumn, compare, sparkline };
}

/** Resolves the tile's configured value column from the row and formats it. */
export function resolveStatValue(
  row: Record<string, unknown> | undefined,
  valueColumn: string,
  format: StatFormat,
): StatValueOutcome {
  const resolved = readNumericColumn(row, valueColumn);
  if (!resolved.ok) return resolved;
  return { ok: true, raw: resolved.value, text: formatStatNumber(resolved.value, format) };
}

function formatSignedPercent(deltaPercent: number): string {
  const rounded = Number(deltaPercent.toFixed(1));
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded}%`;
}

function directionOf(deltaPercent: number): 'up' | 'down' | 'flat' {
  if (deltaPercent > 0) return 'up';
  if (deltaPercent < 0) return 'down';
  return 'flat';
}

/** % change of `current` relative to `base`; a zero base is handled explicitly (never Infinity/NaN). */
function percentChange(current: number, base: number): number {
  if (base === 0) return current === 0 ? 0 : current > 0 ? 100 : -100;
  return ((current - base) / Math.abs(base)) * 100;
}

/** Computes the compare-chip delta for a stat tile's `StatCompare` config. */
export function computeStatCompare(
  currentRaw: number,
  compare: StatCompare,
  row: Record<string, unknown> | undefined,
): StatCompareOutcome {
  if (compare.kind === 'none') return { kind: 'none' };
  if (compare.kind === 'target') {
    const deltaPercent = percentChange(currentRaw, compare.value);
    return { kind: 'delta', deltaPercent, direction: directionOf(deltaPercent), text: formatSignedPercent(deltaPercent) };
  }
  // previous-period: reads the sibling column off the SAME row.
  const resolved = readNumericColumn(row, compare.column);
  if (!resolved.ok) return { kind: 'error', error: resolved.error };
  const deltaPercent = percentChange(currentRaw, resolved.value);
  return { kind: 'delta', deltaPercent, direction: directionOf(deltaPercent), text: formatSignedPercent(deltaPercent) };
}
